import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Zap, AlertTriangle, ShieldCheck, Loader2, CheckCircle2, XCircle, Clock, Copy, Ticket, Share2, MessageCircle, Send, Twitter } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import ShareCard from "./ShareCard";

const MAX_LEGS = 15;

const DISCLAIMER_TEXT = `I understand that:
• NDAKUBET is a prediction tool — it cannot guarantee winnings.
• Automating bets may violate Betway's Terms of Service and could result in account suspension.
• I am legally responsible for my Betway account and any wagers placed from it.
• Sports betting carries a real risk of losing the staked amount.
• I meet the legal gambling age in my jurisdiction.`;

const confTone = (c) =>
  c >= 75 ? "bg-success/15 text-success border-success/40"
  : c >= 60 ? "bg-warning/15 text-warning border-warning/40"
  : "bg-destructive/15 text-destructive border-destructive/40";

export const BestParlayModal = ({ open, onOpenChange, predictions }) => {
  const { user, token } = useAuth();
  const [accountLinked, setAccountLinked] = useState(null);
  const [selected, setSelected] = useState({});
  const [stake, setStake] = useState(10);
  const [accepted, setAccepted] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [betId, setBetId] = useState(null);
  const [betDoc, setBetDoc] = useState(null);
  const [action, setAction] = useState("place");

  useEffect(() => {
    if (open) {
      setSelected({}); setAccepted(false); setBetId(null);
      setBetDoc(null); setStake(10); setAction("place");
    }
  }, [open]);

  useEffect(() => {
    if (!open || !token) return;
    (async () => {
      try { setAccountLinked(!!(await axios.get(`${API}/betway/account`)).data.linked); }
      catch { setAccountLinked(false); }
    })();
  }, [open, token]);

  const rows = useMemo(() => {
    const out = [];
    const isUnlisted = (league, home, away) =>
      /(\bres\b|\breserve|\bu19\b|\bu20\b|\bu21\b|\bu23\b|youth|friendly|women|w\b)/
        .test(`${league || ""} ${home || ""} ${away || ""}`.toLowerCase());
    (predictions || []).forEach((item) => {
      const m = item?.match; if (!m) return;
      const home = m.home_team?.name || ""; const away = m.away_team?.name || "";
      const league = m.league?.name || "";
      if (isUnlisted(league, home, away)) return;
      const leagueCountry = m.league?.country || "";
      (item.top_predictions || []).forEach((p, idx) => {
        const conf = Number(p.confidence) || 0;
        if (conf < 55) return;
        const mt = p.market_type || "";
        const isHalftime = /^(ht |halftime|half time|1st half|first half)/i.test(mt);
        out.push({
          key: `${m.fixture_id}-${idx}`,
          match_id: String(m.fixture_id || m.id || ""),
          home_team: home, away_team: away, league, league_country: leagueCountry,
          market_label: mt, pick: p.prediction || "",
          odds: p.odds ?? null, confidence: conf, risk: p.risk_level || "",
          edge: p.edge ?? null,
          rationale: p.rationale || "",
          kickoff: m.date,
          kind: isHalftime ? "HT" : "FT",
        });
      });
    });
    out.sort((a, b) => b.confidence - a.confidence);
    return out;
  }, [predictions]);

  // Market-filter tabs (All / FT / HT)
  const [marketTab, setMarketTab] = useState("all");
  const filteredRows = useMemo(() => {
    if (marketTab === "ft") return rows.filter((r) => r.kind === "FT");
    if (marketTab === "ht") return rows.filter((r) => r.kind === "HT");
    return rows;
  }, [rows, marketTab]);

  const toggleSelect = (row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.key]) delete next[row.key];
      else {
        if (Object.keys(next).length >= MAX_LEGS) { toast.warning(`Max ${MAX_LEGS} legs per parlay`); return prev; }
        next[row.key] = row;
      }
      return next;
    });
  };

  const selectTopN = (n) => {
    const picks = {};
    filteredRows.slice(0, Math.min(n, MAX_LEGS)).forEach((r) => (picks[r.key] = r));
    setSelected(picks);
  };

  const selectedList = Object.values(selected);
  const combinedOdds = selectedList.reduce((acc, s) => (s.odds && s.odds > 0 ? acc * s.odds : acc), 1);
  const potential = stake * combinedOdds;

  const submit = async () => {
    if (!user) return toast.error("Please log in first");
    if (!accountLinked) return toast.error("Link your Betway account first");
    if (selectedList.length === 0) return toast.error("Pick at least one selection");
    if (!accepted) return toast.error("You must accept the disclaimer");
    if (!stake || stake <= 0) return toast.error("Enter a valid stake");
    setPlacing(true);
    try {
      const payload = {
        stake: Number(stake),
        bet_type: selectedList.length > 1 ? "accumulator" : "single",
        accepted_disclaimer: true, action,
        selections: selectedList.map((s) => ({
          match_id: s.match_id, home_team: s.home_team, away_team: s.away_team,
          market_label: s.market_label, pick: s.pick, odds: s.odds, confidence: s.confidence,
          league: s.league, country: s.league_country,
        })),
      };
      const { data } = await axios.post(`${API}/betway/parlay`, payload);
      setBetId(data.bet_id);
      toast.success(action === "booking_code" ? "Generating booking code…" : "Parlay submitted. Automation running…");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit parlay"); }
    finally { setPlacing(false); }
  };

  useEffect(() => {
    if (!betId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/betway/parlay/${betId}`);
        if (cancelled) return;
        setBetDoc(data);
        if (data.status === "placed" || data.status === "failed") return;
        setTimeout(poll, 3000);
      } catch { setTimeout(poll, 5000); }
    };
    poll();
    return () => { cancelled = true; };
  }, [betId]);

  const renderStatus = () => {
    if (!betDoc) {
      return (
        <div className="flex items-center gap-3 text-muted-foreground py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Connecting to Betway…
        </div>
      );
    }
    const icon = {
      pending: <Clock className="w-5 h-5 text-warning" />,
      running: <Loader2 className="w-5 h-5 animate-spin text-primary" />,
      placed:  <CheckCircle2 className="w-5 h-5 text-success" />,
      failed:  <XCircle className="w-5 h-5 text-destructive" />,
    }[betDoc.status] || <Clock className="w-5 h-5" />;
    return (
      <div className="space-y-4" data-testid="parlay-status">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <div className="font-display font-semibold capitalize">{betDoc.status}</div>
            <div className="text-xs text-muted-foreground">stage: {betDoc.stage}</div>
          </div>
        </div>
        <div className="text-sm rounded-lg border border-border bg-subtle p-3">
          {betDoc.message || "…"}
        </div>
        {betDoc.booking_code && (
          <div className="rounded-xl border border-success/40 bg-success/10 p-4 space-y-2" data-testid="booking-code-block">
            <div className="flex items-center gap-2 text-success text-sm font-semibold">
              <Ticket className="w-4 h-4" /> Betway booking code
            </div>
            <div className="flex items-center gap-3">
              <code className="text-2xl font-tabular font-bold tracking-widest" data-testid="booking-code-value">
                {betDoc.booking_code}
              </code>
              <Button
                size="sm" type="button" variant="outline"
                data-testid="booking-code-copy-btn"
                onClick={() => { navigator.clipboard?.writeText(betDoc.booking_code); toast.success("Booking code copied"); }}
                className="h-8 rounded-full px-3 border-border bg-card text-foreground hover:bg-muted"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Open the Bet Slip → "Enter Booking Code" → paste → place the bet yourself.
            </div>
          </div>
        )}
        {betDoc.bet_id_betway && (
          <div className="text-sm text-success">
            Betway receipt: <span className="font-tabular">{betDoc.bet_id_betway}</span>
          </div>
        )}

        {/* Social-proof share card — shown on 'placed' / 'running' parlays only */}
        {(betDoc.status === "placed" || betDoc.status === "running") && (
          <ShareCard betId={betDoc.id || betId} />
        )}

        {betDoc.screenshot_b64 && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">View Betway screenshot</summary>
            <img alt="Betway state" src={`data:image/jpeg;base64,${betDoc.screenshot_b64}`}
                 className="mt-2 rounded-lg border border-border max-h-96" />
          </details>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="best-parlay-modal"
        className="max-w-3xl bg-background border-border text-foreground max-h-[90vh] overflow-hidden flex flex-col rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold tracking-[-0.02em]">
            <Zap className="w-5 h-5 text-primary" />
            Best Bet Parlay
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Pick your highest-confidence selections, set a stake, and the bot will place the parlay on your linked Betway account.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="py-10 text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-warning mx-auto" />
            <p className="text-muted-foreground">You need an account to place automated parlays.</p>
            <Link to="/auth">
              <Button data-testid="parlay-signup-redirect"
                className="h-10 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90">
                Sign up / Log in
              </Button>
            </Link>
          </div>
        ) : accountLinked === false ? (
          <div className="py-10 text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-warning mx-auto" />
            <p className="text-muted-foreground">Link your Betway account to continue.</p>
            <Link to="/account">
              <Button data-testid="parlay-link-account-redirect"
                className="h-10 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90">
                Link Betway account
              </Button>
            </Link>
          </div>
        ) : betId ? (
          renderStatus()
        ) : (
          <>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="eyebrow">Pick up to {MAX_LEGS} legs — sorted by confidence</div>
              <div className="flex gap-2">
                {[3, 5, 8].map((n) => (
                  <Button
                    key={n} size="sm" variant="outline"
                    onClick={() => selectTopN(n)}
                    data-testid={`parlay-auto-top-${n}`}
                    className="h-8 rounded-full px-3 border-border bg-card text-foreground hover:bg-muted"
                  >
                    Auto top {n}
                  </Button>
                ))}
              </div>
            </div>

            {/* Market-type tabs: All / Full-Time / Halftime */}
            <div className="flex items-center gap-2 mb-3" role="tablist" data-testid="parlay-market-tabs">
              {[
                { key: "all", label: `All (${rows.length})` },
                { key: "ft",  label: `Full-Time (${rows.filter((r) => r.kind === "FT").length})` },
                { key: "ht",  label: `Halftime (${rows.filter((r) => r.kind === "HT").length})` },
              ].map((t) => (
                <button
                  key={t.key} type="button" role="tab"
                  data-testid={`parlay-market-tab-${t.key}`}
                  onClick={() => setMarketTab(t.key)}
                  className={`text-xs px-3 h-8 rounded-full border transition-colors ${
                    marketTab === t.key
                      ? "bg-primary text-primary-foreground border-primary/50"
                      : "bg-card border-border text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <ScrollArea className="h-80 border border-border rounded-xl mb-4" data-testid="parlay-rows">
              {filteredRows.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  No {marketTab === "ht" ? "halftime" : marketTab === "ft" ? "full-time" : ""} predictions available.
                  Reserve/youth/friendly matches are filtered out.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {filteredRows.map((r) => {
                    const isOn = !!selected[r.key];
                    const riskTone =
                      r.risk === "Low" ? "text-success" :
                      r.risk === "High" ? "text-destructive" : "text-warning";
                    return (
                      <li
                        key={r.key}
                        onClick={() => toggleSelect(r)}
                        data-testid={`parlay-row-${r.key}`}
                        className={`p-3 cursor-pointer transition-colors ${
                          isOn ? "bg-primary/10" : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <Checkbox checked={isOn} onCheckedChange={() => toggleSelect(r)} />
                            <div className="min-w-0">
                              <div className="text-sm font-display font-semibold truncate">
                                {r.home_team} <span className="text-muted-foreground font-normal">vs</span> {r.away_team}
                                {r.kind === "HT" && (
                                  <span className="ml-2 text-[10px] font-tabular px-1.5 py-0.5 rounded bg-accent/20 text-accent uppercase tracking-wider border border-accent/30">
                                    HT
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                <span className="mr-1" aria-hidden>{countryToFlag(r.league_country)}</span>
                                {r.league} · <span className="font-semibold text-foreground/80">{r.market_label}</span>:{" "}
                                <span className="text-foreground">{r.pick}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {r.odds ? <span className="text-xs text-muted-foreground font-tabular">@{Number(r.odds).toFixed(2)}</span> : null}
                            {r.edge !== null && r.edge !== undefined ? (
                              <span className="text-[10px] font-tabular text-success/80">edge {Number(r.edge).toFixed(1)}%</span>
                            ) : null}
                            <Badge className={`border ${confTone(r.confidence)}`}>{Math.round(r.confidence)}%</Badge>
                          </div>
                        </div>
                        {r.rationale ? (
                          <div className="mt-1.5 ml-7 text-[11px] text-muted-foreground leading-tight line-clamp-2">
                            <span className={`font-semibold ${riskTone}`}>{r.risk || "—"} risk</span>
                            {" · "}
                            {r.rationale}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </ScrollArea>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="eyebrow">Stake</Label>
                <Input
                  type="number" min={0.5} step={0.5} value={stake}
                  onChange={(e) => setStake(e.target.value)}
                  data-testid="parlay-stake"
                  className="mt-1.5 bg-subtle border-border h-10 rounded-xl"
                />
              </div>
              <div className="text-sm text-muted-foreground self-end" data-testid="parlay-summary">
                <div>
                  <span>Legs:</span> <span className="text-foreground font-tabular">{selectedList.length}</span>
                  <span className="mx-2">·</span>
                  <span>Odds:</span>{" "}
                  <span className="text-foreground font-tabular">{combinedOdds > 1 ? combinedOdds.toFixed(2) : "—"}</span>
                </div>
                <div className="text-success font-semibold font-tabular">
                  Potential: {combinedOdds > 1 && stake ? potential.toFixed(2) : "—"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup" data-testid="parlay-action-toggle">
              <button
                type="button" role="radio" aria-checked={action === "place"}
                onClick={() => setAction("place")}
                data-testid="action-place"
                className={`p-3 rounded-xl border text-left transition-colors ${
                  action === "place"
                    ? "bg-primary/10 border-primary/50 text-foreground"
                    : "bg-card border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Zap className="w-4 h-4" /> Auto-place on Betway
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Bot logs in, fills the slip, sets stake, clicks Place Bet.
                </div>
              </button>
              <button
                type="button" role="radio" aria-checked={action === "booking_code"}
                onClick={() => setAction("booking_code")}
                data-testid="action-booking-code"
                className={`p-3 rounded-xl border text-left transition-colors ${
                  action === "booking_code"
                    ? "bg-success/10 border-success/50 text-foreground"
                    : "bg-card border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Ticket className="w-4 h-4" /> Booking code only
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Bot builds the slip then returns a share code — you place it yourself.
                </div>
              </button>
            </div>

            <div
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 mb-3 text-xs text-foreground whitespace-pre-line"
              data-testid="parlay-disclaimer"
            >
              {DISCLAIMER_TEXT}
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer mb-2" data-testid="parlay-disclaimer-label">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="parlay-disclaimer-check" />
              <span>I understand and accept the disclaimer above.</span>
            </label>

            <DialogFooter className="mt-2 gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="parlay-cancel-btn"
                className="h-10 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={placing || selectedList.length === 0 || !accepted}
                data-testid="parlay-submit-btn"
                className="h-10 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                {placing
                  ? "Submitting…"
                  : action === "booking_code"
                  ? `Generate code · ${selectedList.length} leg${selectedList.length === 1 ? "" : "s"}`
                  : `Place ${selectedList.length}-leg parlay`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BestParlayModal;
